import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import type { SettingsSection } from '../nav';
import { Icon } from '@/shared/ui/atoms/Icon';
import '../Sidebar/Sidebar.styl';

type Props = {
  to: string;
  label: string;
  sections: readonly SettingsSection[];
};

/**
 * "Settings" in the sidebar with its sections as sub-items. Unfolded while a settings page is open; the
 * chevron folds it. The current section comes from `?section=` (the page falls back to the first one).
 */
export function SettingsMenu({ to, label, sections }: Props) {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const onSettings = pathname === to;
  const current = new URLSearchParams(search).get('section') ?? sections[0]?.id;
  const [folded, setFolded] = useState(false);
  const open = onSettings && !folded;

  return (
    <div className="w-sidebar__group">
      <div
        className={
          onSettings
            ? 'w-sidebar__item w-sidebar__item--active w-sidebar__item--parent'
            : 'w-sidebar__item w-sidebar__item--parent'
        }
      >
        <Link
          to={to}
          className="w-sidebar__parent-link"
          onClick={() => setFolded(false)}
          aria-current={onSettings ? 'page' : undefined}
        >
          <Icon name="settings" />
          <span className="w-sidebar__item-label">{label}</span>
        </Link>
        {onSettings ? (
          <button
            type="button"
            className="w-sidebar__fold"
            aria-expanded={open}
            aria-label={label}
            onClick={() => setFolded(!folded)}
          >
            <Icon
              name="chevronDown"
              size={14}
              strokeWidth={2}
              className={
                open
                  ? 'w-sidebar__chevron w-sidebar__chevron--open'
                  : 'w-sidebar__chevron'
              }
            />
          </button>
        ) : null}
      </div>
      {open ? (
        <ul className="w-sidebar__sub" aria-label={label}>
          {sections.map((s) => {
            const active = current === s.id;
            return (
              <li key={s.id}>
                <Link
                  to={`${to}?section=${s.id}`}
                  className={
                    active
                      ? 'w-sidebar__subitem w-sidebar__subitem--active'
                      : 'w-sidebar__subitem'
                  }
                  aria-current={active ? 'page' : undefined}
                >
                  {t(s.label as never)}
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
